/**
 * CheckoutWorkflowDrawer — multi-step guided checkout flow.
 *
 * Steps:
 *   1. Review stay — dates, pet name, run assigned.
 *   2. Adjust checkout date — optional actual checkout (early OR late) with
 *      bidirectional engine-driven quote preview.
 *   3. Payment — record cash/cheque/manual payment, charge card on file, or
 *      take card via Stripe.js Payment Element (StaffCardPaymentPanel).
 *   4. Confirm & check out — summary then POST /staff/bookings/:id/checkout.
 *
 * Used from BookingDetail > PetSection when staff click "Check out".
 * Replaces the previous direct `staffCheckoutBooking(id)` call.
 */
import { useAuthContext } from '@/auth/useAuthContext';
import {
  ApiError,
  type BookingGroupEntry,
  type EarlyCheckoutPreview,
  getEarlyCheckoutPreview,
  listBookingPayments,
  staffChargeBalance,
  staffChargeBookingManual,
  staffCheckoutBooking,
} from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';
import { useSpaceNoun } from '@/lib/useSpaceNoun';
import {
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Loader2,
  LogOut,
  Moon,
  X,
  Zap,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { StaffCardPaymentPanel } from './StaffCardPaymentPanel';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CheckoutWorkflowDrawerProps {
  bookingId: string;
  entry: BookingGroupEntry;
  currency: string;
  totalMinor: number;
  onClose: () => void;
  onCheckedOut: () => void;
}

// ---------------------------------------------------------------------------
// Step labels
// ---------------------------------------------------------------------------

const STEPS = ['Review Stay', 'Adjust checkout date?', 'Payment', 'Confirm & Check Out'];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={`h-1.5 flex-1 rounded-full transition-colors ${
            i < current ? 'bg-emerald-500' : i === current ? 'bg-blue-600' : 'bg-border'
          }`}
        />
      ))}
    </div>
  );
}

function formatDateLocal(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** YYYY-MM-DD for "now" in the facility's zone (falls back to the browser). */
/**
 * Map a failed checkout into copy staff can act on. ApiError's default
 * message is the raw `"<status> <code>"` pair, which is what used to render.
 */
export function describeCheckoutError(e: unknown): string {
  const code = e instanceof ApiError ? e.code : null;
  const detail =
    e instanceof ApiError && typeof e.body?.message === 'string' ? ` (${e.body.message})` : '';
  switch (code) {
    case 'quote_error':
      return `The stay could not be re-priced for the extra nights${detail}. Check the boarding service and overstay policy, or check out without adjusting the date.`;
    case 'invalid_transition':
      return 'This booking is not checked in, so it cannot be checked out.';
    case 'booking_not_found':
      return 'This booking no longer exists — it may have been deleted.';
    case 'invalid_body':
    case 'invalid_json':
      return 'The checkout date could not be read. Pick the date again and retry.';
    case 'forbidden':
      return 'You do not have permission to check out bookings.';
    default:
      return e instanceof ApiError
        ? `Checkout failed (${e.code}). The booking may still have been checked out — refresh to confirm.`
        : ((e as Error).message ?? 'Checkout failed');
  }
}

export function todayInTz(timezone: string | null | undefined, now: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone ?? undefined,
      dateStyle: 'short',
    }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

function toUtcMs(day: string): number {
  const [y, m, d] = day.split('-').map((n) => Number.parseInt(n, 10)) as [number, number, number];
  return Date.UTC(y, m - 1, d);
}

/** Whole calendar days from `a` to `b` (negative when b < a). */
export function diffCalendarDays(a: string, b: string): number {
  return Math.round((toUtcMs(b) - toUtcMs(a)) / 86_400_000);
}

/**
 * Migration 0103 — the facility-local date is past the scheduled checkout
 * day and the pet is still checked in: the drawer pre-fills today as the
 * actual checkout so the extra nights get priced instead of silently lost.
 */
export function detectOverstay(
  entry: Pick<BookingGroupEntry, 'booking' | 'stay'>,
  localToday: string,
): { scheduledDate: string; extraNights: number } | null {
  if (!entry.stay || entry.booking.status !== 'checked_in') return null;
  const scheduledDate = entry.stay.checkout.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(scheduledDate) || localToday <= scheduledDate) return null;
  return { scheduledDate, extraNights: diffCalendarDays(scheduledDate, localToday) };
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CheckoutWorkflowDrawer({
  bookingId,
  entry,
  currency,
  totalMinor,
  onClose,
  onCheckedOut,
}: CheckoutWorkflowDrawerProps) {
  const noun = useSpaceNoun();
  const [step, setStep] = useState(0);

  // Facility-local "today" — the same calendar the overstay cron uses. Safe
  // outside AuthProvider (tests): useAuthContext registers its useContext
  // before throwing, so hook order is stable.
  let facilityTimezone: string | null = null;
  try {
    const me = useAuthContext().state.me;
    facilityTimezone = me?.activeFacility?.timezone ?? me?.activeAccount?.defaultTimezone ?? null;
  } catch {
    facilityTimezone = null;
  }
  const localToday = todayInTz(facilityTimezone);
  // Migration 0103 — checked in past the scheduled checkout day.
  const overstay = detectOverstay(entry, localToday);
  const chargedOverstayItems = entry.lineItems.filter((li) => li.kind === 'overstay_night');

  // Step 2 — adjust checkout date state (bidirectional: early OR late).
  // Overstaying stays default to "adjust to today" so the extra nights are
  // priced; staff can still untick or pick another date.
  const [adjustCheckout, setAdjustCheckout] = useState(() => overstay !== null);
  const [actualCheckoutDate, setActualCheckoutDate] = useState(() => (overstay ? localToday : ''));
  const [checkoutPreview, setCheckoutPreview] = useState<EarlyCheckoutPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Step 3 — payment state
  const [paidMinor, setPaidMinor] = useState(0);
  const [paymentSource, setPaymentSource] = useState<'cash' | 'check' | 'manual'>('cash');
  const [paymentAmountStr, setPaymentAmountStr] = useState('');
  const [paymentRecorded, setPaymentRecorded] = useState(false);
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  // Charge card on file state
  const [chargingCard, setChargingCard] = useState(false);
  const [chargeCardError, setChargeCardError] = useState<string | null>(null);
  // Take card payment via Stripe.js
  const [showCardPanel, setShowCardPanel] = useState(false);

  // Overall
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { pet, stay } = entry;

  // Effective total: use the engine-driven preview when the date was adjusted
  // (works bidirectionally — early checkout gives fewer nights, late checkout
  // gives more nights and a higher total).
  const effectiveTotalMinor = checkoutPreview?.adjustedQuoteMinor ?? totalMinor;

  // Pre-load paid amount from payments API so staff see accurate balance.
  useEffect(() => {
    listBookingPayments(bookingId)
      .then((r) => {
        const paid = r.payments
          .filter((p) => p.direction === 'in' && p.status === 'succeeded')
          .reduce((acc, p) => acc + p.amountMinor, 0);
        setPaidMinor(paid);
      })
      .catch(() => void 0);
  }, [bookingId]);

  // Overstay auto-detect: load the engine preview for today straight away so
  // the payment step already shows the real total.
  const overstayDetected = overstay !== null;
  useEffect(() => {
    if (!overstayDetected) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    getEarlyCheckoutPreview(bookingId, localToday)
      .then((result) => {
        if (!cancelled) setCheckoutPreview(result);
      })
      .catch((e: unknown) => {
        if (!cancelled) setPreviewError((e as Error).message ?? 'Could not load preview');
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bookingId, localToday, overstayDetected]);

  // --- Checkout date preview fetch (bidirectional) ---

  async function fetchCheckoutPreview(dateStr: string) {
    if (!dateStr) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const result = await getEarlyCheckoutPreview(bookingId, dateStr);
      setCheckoutPreview(result);
    } catch (e: unknown) {
      setPreviewError((e as Error).message ?? 'Could not load preview');
      setCheckoutPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }

  // --- Record manual payment ---

  async function handleRecordPayment() {
    const amountMinor = Math.round(Number.parseFloat(paymentAmountStr) * 100);
    if (!Number.isFinite(amountMinor) || amountMinor <= 0) {
      setPaymentError('Enter a valid amount');
      return;
    }
    setRecordingPayment(true);
    setPaymentError(null);
    try {
      await staffChargeBookingManual(bookingId, { amountMinor, source: paymentSource });
      setPaymentRecorded(true);
      setPaidMinor((p) => p + amountMinor);
      setPaymentAmountStr('');
    } catch (e: unknown) {
      setPaymentError((e as Error).message ?? 'Payment failed');
    } finally {
      setRecordingPayment(false);
    }
  }

  // --- Charge card on file (off-session) ---

  async function handleChargeCard() {
    setChargingCard(true);
    setChargeCardError(null);
    try {
      await staffChargeBalance(bookingId);
      // Reload paid amount so the outstanding balance updates.
      const r = await listBookingPayments(bookingId);
      const paid = r.payments
        .filter((p) => p.direction === 'in' && p.status === 'succeeded')
        .reduce((acc, p) => acc + p.amountMinor, 0);
      setPaidMinor(paid);
      setPaymentRecorded(true);
    } catch (e: unknown) {
      const code = e instanceof ApiError ? e.code : null;
      const friendly =
        code === 'no_default_pm'
          ? 'No saved card on file — take payment another way.'
          : code === 'connect_off' || code === 'stripe_off'
            ? 'Stripe is not configured for this account. Take payment another way.'
            : code === 'pi_failed'
              ? "Card charge failed. Check the customer's card details or take payment another way."
              : ((e as Error).message ?? 'Charge failed');
      setChargeCardError(friendly);
    } finally {
      setChargingCard(false);
    }
  }

  // --- Confirm & check out ---

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      // Pass actual checkout date when adjusted. The API re-prices the stay
      // and persists the updated total before flipping the booking to
      // checked_out (bidirectional: works for both early and late departure).
      const actualCheckout =
        adjustCheckout && actualCheckoutDate
          ? actualCheckoutDate.length === 10
            ? `${actualCheckoutDate}T12:00:00`
            : actualCheckoutDate
          : undefined;
      await staffCheckoutBooking(bookingId, actualCheckout ? { actualCheckout } : undefined);
      onCheckedOut();
    } catch (e: unknown) {
      // The booking is already checked out (a retry, or another device beat
      // us to it) — treat as done rather than surfacing "409 invalid_transition".
      if (e instanceof ApiError && e.status === 409) {
        onCheckedOut();
        return;
      }
      setError(describeCheckoutError(e));
    } finally {
      setBusy(false);
    }
  }

  // --- Step content ---

  function renderStep() {
    switch (step) {
      case 0:
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Stay Summary</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Review stay details before proceeding.
              </p>
            </div>

            <div className="rounded-lg border border-border bg-background divide-y divide-border">
              <div className="px-4 py-3">
                <p className="text-xs text-muted-foreground">Pet</p>
                <p className="mt-0.5 text-sm font-semibold text-foreground">
                  {pet.name}
                  {pet.breed ? ` — ${pet.breed}` : ''}
                </p>
              </div>

              {stay && (
                <>
                  <div className="px-4 py-3">
                    <p className="text-xs text-muted-foreground">Check-in</p>
                    <p className="num mt-0.5 text-sm text-foreground">
                      {formatDateLocal(stay.checkIn)}
                    </p>
                  </div>
                  <div className="px-4 py-3">
                    <p className="text-xs text-muted-foreground">Scheduled checkout</p>
                    <p className="num mt-0.5 text-sm text-foreground">
                      {formatDateLocal(stay.checkout)}
                    </p>
                    {overstay && (
                      <p className="num mt-1 text-xs text-destructive" data-testid="overstay-note">
                        Still checked in — {overstay.extraNights} extra night
                        {overstay.extraNights !== 1 ? 's' : ''} so far
                      </p>
                    )}
                  </div>
                  {stay.runName && (
                    <div className="px-4 py-3">
                      <p className="text-xs text-muted-foreground">{noun.Singular}</p>
                      <p className="mt-0.5 text-sm text-foreground">{stay.runName}</p>
                    </div>
                  )}
                </>
              )}

              <div className="px-4 py-3">
                <p className="text-xs text-muted-foreground">Total</p>
                <p className="num mt-0.5 text-sm font-semibold text-foreground">
                  {formatPriceMinor(totalMinor, currency)}
                </p>
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Adjust Checkout Date</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                If the actual departure date differs from the scheduled date (early or late), enter
                it here. The price will be recalculated automatically.
              </p>
            </div>

            {overstay && (
              <div
                className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
                data-testid="overstay-banner"
              >
                <Moon className="mt-0.5 size-3.5 shrink-0 text-amber-700" strokeWidth={1.75} />
                <p className="num text-xs text-amber-900">
                  Scheduled checkout was {formatDateLocal(overstay.scheduledDate)} —{' '}
                  {overstay.extraNights} extra night{overstay.extraNights !== 1 ? 's' : ''}. Today
                  has been pre-filled as the actual checkout.
                </p>
              </div>
            )}

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={adjustCheckout}
                  onChange={(e) => {
                    setAdjustCheckout(e.target.checked);
                    if (!e.target.checked) {
                      setCheckoutPreview(null);
                      setActualCheckoutDate('');
                    }
                  }}
                  className="size-4 rounded border-border accent-blue-600"
                />
                <span className="text-sm text-foreground">Actual date differs from scheduled</span>
              </label>
            </div>

            {adjustCheckout && (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">
                    Actual checkout date
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={actualCheckoutDate}
                      onChange={(e) => {
                        setActualCheckoutDate(e.target.value);
                        setCheckoutPreview(null);
                      }}
                      className="num flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
                    />
                    <button
                      type="button"
                      onClick={() => void fetchCheckoutPreview(actualCheckoutDate)}
                      disabled={!actualCheckoutDate || previewLoading}
                      className="flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium hover:bg-secondary disabled:opacity-50"
                    >
                      {previewLoading ? (
                        <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />
                      ) : (
                        <Calendar className="size-3.5" strokeWidth={1.75} />
                      )}
                      Preview
                    </button>
                  </div>
                </div>

                {previewError && <p className="text-xs text-destructive">{previewError}</p>}

                {checkoutPreview && (
                  <div className="rounded-lg border border-border bg-secondary px-4 py-3 space-y-2">
                    <div className="flex justify-between">
                      <span className="text-xs text-muted-foreground">Original total</span>
                      <span className="num text-sm text-muted-foreground line-through">
                        {formatPriceMinor(totalMinor, currency)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-muted-foreground">
                        Adjusted ({checkoutPreview.chargedNights ?? checkoutPreview.actualNights}{' '}
                        night
                        {(checkoutPreview.chargedNights ?? checkoutPreview.actualNights) !== 1
                          ? 's'
                          : ''}
                        )
                      </span>
                      <span
                        className={`num text-sm font-semibold ${checkoutPreview.adjustedQuoteMinor > totalMinor ? 'text-destructive' : 'text-foreground'}`}
                      >
                        {formatPriceMinor(checkoutPreview.adjustedQuoteMinor, currency)}
                      </span>
                    </div>
                    {checkoutPreview.nightsSaved > 0 && (
                      <div className="flex justify-between border-t border-border pt-2">
                        <span className="text-xs text-emerald-700">Saving</span>
                        <span className="num text-xs font-medium text-emerald-700">
                          {formatPriceMinor(
                            totalMinor - checkoutPreview.adjustedQuoteMinor,
                            currency,
                          )}
                        </span>
                      </div>
                    )}
                    {checkoutPreview.adjustedQuoteMinor > totalMinor && (
                      <div className="flex justify-between border-t border-border pt-2">
                        <span className="text-xs text-destructive">Extra charge</span>
                        <span className="num text-xs font-medium text-destructive">
                          +
                          {formatPriceMinor(
                            checkoutPreview.adjustedQuoteMinor - totalMinor,
                            currency,
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {chargedOverstayItems.length > 0 && (
                  <div
                    className="rounded-lg border border-border bg-background px-4 py-3"
                    data-testid="overstay-charged-list"
                  >
                    <p className="text-xs font-medium text-foreground">
                      Extra nights already charged
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Added automatically by your overstay policy. The adjusted total above includes
                      them — they are never counted twice.
                    </p>
                    <div className="mt-2 divide-y divide-border/50">
                      {chargedOverstayItems.map((li) => (
                        <div key={li.id} className="flex items-center justify-between py-1 text-xs">
                          <span className="num text-foreground">
                            Night of{' '}
                            {li.scheduledDate ? formatDateLocal(li.scheduledDate) : 'unknown date'}
                          </span>
                          <span className="num text-muted-foreground">
                            {formatPriceMinor(li.priceMinor, currency)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 2: {
        const newOutstanding = Math.max(0, effectiveTotalMinor - paidMinor);
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Payment</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Record any outstanding payment before checking out.
              </p>
            </div>

            {/* Balance summary */}
            <div className="rounded-lg border border-border bg-background divide-y divide-border">
              <div className="flex justify-between px-4 py-3">
                <span className="text-xs text-muted-foreground">Total</span>
                <span className="num text-sm font-semibold text-foreground">
                  {formatPriceMinor(effectiveTotalMinor, currency)}
                </span>
              </div>
              <div className="flex justify-between px-4 py-3">
                <span className="text-xs text-muted-foreground">Already paid</span>
                <span className="num text-sm text-emerald-700">
                  {formatPriceMinor(paidMinor, currency)}
                </span>
              </div>
              <div className="flex justify-between px-4 py-3">
                <span className="text-xs text-muted-foreground">Outstanding</span>
                <span
                  className={`num text-sm font-semibold ${newOutstanding > 0 ? 'text-destructive' : 'text-emerald-700'}`}
                >
                  {newOutstanding > 0 ? formatPriceMinor(newOutstanding, currency) : 'Paid in full'}
                </span>
              </div>
            </div>

            {paymentRecorded && (
              <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5">
                <CheckCircle className="size-3.5 text-emerald-600 shrink-0" strokeWidth={1.75} />
                <span className="text-xs font-medium text-emerald-800">Payment recorded</span>
              </div>
            )}

            {newOutstanding > 0 && !showCardPanel && (
              <div className="space-y-3">
                {/* Charge card on file */}
                <div className="rounded-lg border border-border bg-background p-4 space-y-2">
                  <p className="text-xs font-medium text-foreground">Card on file</p>
                  <p className="text-xs text-muted-foreground">
                    Charge the customer's saved card for the outstanding balance.
                  </p>
                  {chargeCardError && <p className="text-xs text-destructive">{chargeCardError}</p>}
                  <button
                    type="button"
                    onClick={() => void handleChargeCard()}
                    disabled={chargingCard}
                    className="flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                  >
                    {chargingCard ? (
                      <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />
                    ) : (
                      <Zap className="size-3.5" strokeWidth={1.75} />
                    )}
                    Charge card on file
                  </button>
                </div>

                {/* Take card payment via Stripe.js */}
                <div className="rounded-lg border border-border bg-background p-4">
                  <p className="text-xs font-medium text-foreground mb-1">Card present</p>
                  <p className="text-xs text-muted-foreground mb-2">
                    Customer is present — enter card details directly.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowCardPanel(true)}
                    className="flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100"
                  >
                    <CreditCard className="size-3.5" strokeWidth={1.75} />
                    Take card payment
                  </button>
                </div>

                {/* Record cash / cheque / card recorded elsewhere */}
                <div className="space-y-3 rounded-lg border border-border bg-background p-4">
                  <p className="text-xs font-medium text-foreground">Record payment</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Amount</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={paymentAmountStr}
                        onChange={(e) => setPaymentAmountStr(e.target.value)}
                        placeholder="0.00"
                        className="num w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1">Method</label>
                      <select
                        value={paymentSource}
                        onChange={(e) =>
                          setPaymentSource(e.target.value as 'cash' | 'check' | 'manual')
                        }
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
                      >
                        <option value="cash">Cash</option>
                        <option value="check">Cheque</option>
                        <option value="manual">Card (recorded elsewhere)</option>
                      </select>
                    </div>
                  </div>
                  {paymentError && <p className="text-xs text-destructive">{paymentError}</p>}
                  <button
                    type="button"
                    onClick={() => void handleRecordPayment()}
                    disabled={recordingPayment || !paymentAmountStr}
                    className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    {recordingPayment ? (
                      <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />
                    ) : (
                      <CreditCard className="size-3.5" strokeWidth={1.75} />
                    )}
                    Record payment
                  </button>
                </div>
              </div>
            )}

            {/* Inline Stripe.js card payment panel */}
            {newOutstanding > 0 && showCardPanel && (
              <div className="rounded-lg border border-blue-200 bg-blue-50/30 p-4">
                <StaffCardPaymentPanel
                  bookingId={bookingId}
                  currency={currency}
                  onSuccess={async () => {
                    setShowCardPanel(false);
                    // Refresh paid amount.
                    const r = await listBookingPayments(bookingId);
                    const paid = r.payments
                      .filter((p) => p.direction === 'in' && p.status === 'succeeded')
                      .reduce((acc, p) => acc + p.amountMinor, 0);
                    setPaidMinor(paid);
                    setPaymentRecorded(true);
                  }}
                  onCancel={() => setShowCardPanel(false)}
                />
              </div>
            )}

            {newOutstanding === 0 && (
              <p className="text-xs text-muted-foreground">
                No payment outstanding. Proceed to check out.
              </p>
            )}
          </div>
        );
      }

      case 3: {
        const finalOutstanding = Math.max(0, effectiveTotalMinor - paidMinor);
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Confirm Checkout</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Review the summary then check out {pet.name}.
              </p>
            </div>

            <div className="rounded-lg border border-border bg-background divide-y divide-border">
              <div className="px-4 py-3">
                <p className="text-xs text-muted-foreground">Pet</p>
                <p className="mt-0.5 text-sm font-semibold text-foreground">{pet.name}</p>
              </div>

              {stay && (
                <div className="px-4 py-3">
                  <p className="text-xs text-muted-foreground">Stay</p>
                  <p className="num mt-0.5 text-sm text-foreground">
                    {formatDateLocal(stay.checkIn)}
                    {' — '}
                    {adjustCheckout && actualCheckoutDate
                      ? `${formatDateLocal(actualCheckoutDate)} (adjusted)`
                      : formatDateLocal(stay.checkout)}
                  </p>
                </div>
              )}

              <div className="px-4 py-3">
                <p className="text-xs text-muted-foreground">Charged</p>
                <p className="num mt-0.5 text-sm font-semibold text-foreground">
                  {formatPriceMinor(effectiveTotalMinor, currency)}
                  {checkoutPreview && (
                    <span
                      className={`ml-1.5 text-xs font-normal ${checkoutPreview.adjustedQuoteMinor !== totalMinor ? 'text-emerald-700' : ''}`}
                    >
                      (adjusted)
                    </span>
                  )}
                </p>
              </div>

              <div className="px-4 py-3">
                <p className="text-xs text-muted-foreground">Balance</p>
                <p
                  className={`num mt-0.5 text-sm font-semibold ${finalOutstanding > 0 ? 'text-destructive' : 'text-emerald-700'}`}
                >
                  {finalOutstanding > 0
                    ? `${formatPriceMinor(finalOutstanding, currency)} outstanding`
                    : 'Paid in full'}
                </p>
              </div>
            </div>

            {finalOutstanding > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="text-xs text-amber-800">
                  There is still an outstanding balance. You may proceed, but consider collecting
                  payment before finalising checkout.
                </p>
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  }

  const isLastStep = step === STEPS.length - 1;

  const content = (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/40 sm:items-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-t-2xl sm:rounded-2xl bg-surface ring-1 ring-border max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4 shrink-0">
          <div className="flex items-center gap-2.5">
            <LogOut className="size-4 text-muted-foreground" strokeWidth={1.75} />
            <div>
              <h2 className="text-sm font-semibold text-foreground">Check Out</h2>
              <p className="text-xs text-muted-foreground">{STEPS[step]}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary"
            aria-label="Close"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-5 pt-3 pb-1 shrink-0">
          <StepIndicator current={step} total={STEPS.length} />
          <p className="mt-1.5 text-xs text-muted-foreground">
            Step {step + 1} of {STEPS.length}
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">{renderStep()}</div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border px-5 py-4 shrink-0">
          <button
            type="button"
            onClick={() => {
              if (step === 0) onClose();
              else setStep((s) => s - 1);
            }}
            className="flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium hover:bg-secondary"
          >
            <ChevronLeft className="size-3.5" strokeWidth={1.75} />
            {step === 0 ? 'Cancel' : 'Back'}
          </button>

          {isLastStep ? (
            <button
              type="button"
              onClick={() => void handleConfirm()}
              disabled={busy}
              className="flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {busy ? (
                <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />
              ) : (
                <CheckCircle className="size-3.5" strokeWidth={1.75} />
              )}
              Check Out
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStep((s) => s + 1)}
              className="flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-xs font-medium text-white hover:bg-blue-700"
            >
              Next
              <ChevronRight className="size-3.5" strokeWidth={1.75} />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
